import React, { useState, lazy, Suspense, useEffect } from 'react';
import { useOrders } from '../context/OrderContext';
import { useOutlet } from '../../context/OutletContext';
import { CustomerProvider } from '../context/CustomerContext';
import { UserProvider } from '../context/UserContext';
import { usePermissions } from '../hooks/usePermissions';
import { getHomeViewForRole } from '../utils/roleDashboards';
import OrderCard from '../components/OrderCard';
import OrderAcceptanceCard from '../components/OrderAcceptanceCard';
import OrderDetailsModal from '../components/OrderDetailsModal';
import DeliveryBoyAssignmentModal from '../components/DeliveryBoyAssignmentModal';
import { Header, Sidebar } from '../components/layout';
import StatsCard from '../components/StatsCard';
import Analytics from '../components/Analytics';
import OutletComparison from '../components/OutletComparison';
import AccessDenied from '../components/AccessDenied';
import './Dashboard.css';

// Lazy-loaded page components for code splitting
const Customers = lazy(() => import('./Customers'));
const Users = lazy(() => import('./Users'));
const UserSync = lazy(() => import('./UserSync'));
const OrderDetail = lazy(() => import('./OrderDetail'));
const AnalyticsPage = lazy(() => import('./AnalyticsPage'));
const Reviews = lazy(() => import('./Reviews'));
const Menu = lazy(() => import('./Menu'));
const MenuMigration = lazy(() => import('./MenuMigration'));
const Calendar = lazy(() => import('./Calendar'));
const Chat = lazy(() => import('./Chat'));
const Settings = lazy(() => import('./Settings'));
const ThermalPrinter = lazy(() => import('./ThermalPrinter'));
const PhoneOrder = lazy(() => import('./PhoneOrder'));
const DataManagement = lazy(() => import('./DataManagement'));
const Outlets = lazy(() => import('./Outlets'));
// New redesigned pages
const Orders = lazy(() => import('./Orders'));
const OrderHistory = lazy(() => import('./OrderHistory'));
const MenuEditor = lazy(() => import('./MenuEditor'));
const Inventory = lazy(() => import('./Inventory'));
// Finance pages
const Payouts = lazy(() => import('./Finance/Payouts'));
const Transactions = lazy(() => import('./Finance/Transactions'));
const Invoices = lazy(() => import('./Finance/Invoices'));
const OutletFinanceReports = lazy(() => import('./Finance/OutletFinanceReports'));
// Offers pages
const CreateOffers = lazy(() => import('./Offers/CreateOffers'));
const TrackOffers = lazy(() => import('./Offers/TrackOffers'));
// Outlet Info pages
const AboutOutlet = lazy(() => import('./OutletInfo/AboutOutlet'));
const ContactDetails = lazy(() => import('./OutletInfo/ContactDetails'));
const OutletTimings = lazy(() => import('./OutletInfo/OutletTimings'));
const Location = lazy(() => import('./OutletInfo/Location'));
const SmartLink = lazy(() => import('./OutletInfo/SmartLink'));
// Reporting pages
const LiveTracking = lazy(() => import('./Reporting/LiveTracking'));
const BusinessReports = lazy(() => import('./Reporting/BusinessReports'));
// Items Management pages (Phase 7)
const ListAllItems = lazy(() => import('./Items/ListAllItems'));
// Delivery Management pages (Phase 9)
const DeliveryBoys = lazy(() => import('./Delivery/DeliveryBoys'));
const DeliveryTracking = lazy(() => import('./Delivery/DeliveryTracking'));
// Phase 10 pages
const KitchenDisplay = lazy(() => import('./Kitchen/KitchenDisplay'));
const StockAlerts = lazy(() => import('./Inventory/StockAlerts'));
const ManageLocations = lazy(() => import('./Locations/ManageLocations'));
const RolePermissions = lazy(() => import('./Permissions/RolePermissions'));
// Billing & Accounting pages
const BillingDashboard = lazy(() => import('./Billing/BillingDashboard'));
const CreateInvoice = lazy(() => import('./Billing/CreateInvoice'));
const GSTSettings = lazy(() => import('./Billing/GSTSettings'));
const ExpenseManagement = lazy(() => import('./Accounting/ExpenseManagement'));
const ProfitLoss = lazy(() => import('./Accounting/ProfitLoss'));
const CashFlow = lazy(() => import('./Accounting/CashFlow'));
// New features
const LoyaltyProgram = lazy(() => import('./LoyaltyProgram'));
const RedemptionHistory = lazy(() => import('./RedemptionHistory'));
const ScheduledOrders = lazy(() => import('./ScheduledOrders'));
const SalesForecasting = lazy(() => import('./Analytics/SalesForecasting'));
const CustomerSegmentation = lazy(() => import('./Analytics/CustomerSegmentation'));
const MenuEngineering = lazy(() => import('./Analytics/MenuEngineering'));
const StaffPerformance = lazy(() => import('./Analytics/StaffPerformance'));
const PredictiveInsights = lazy(() => import('./Analytics/PredictiveInsights'));
// POS Management pages
const POSSettings = lazy(() => import('./POSSettings'));
const AdminTableManagement = lazy(() => import('./Tables/AdminTableManagement'));
const AdminTerminalManagement = lazy(() => import('./AdminTerminalManagement'));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="page-loader">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

const Dashboard = () => {
  const {
    orders,
    loading,
    selectedDate,
    setSelectedDate,
    updateOrderStatus,
    assignDeliveryBoy,
    acceptOrder,
    rejectOrder,
    getOrdersByStatus,
    getPendingAcceptanceOrders,
    getStats
  } = useOrders();

  const { viewMode } = useOutlet();
  const { canAccessView, userRole, loading: permissionsLoading } = usePermissions();

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeliveryAssignment, setShowDeliveryAssignment] = useState(false);
  const [orderToAssign, setOrderToAssign] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Redirect to role-specific home view on mount
  useEffect(() => {
    if (!permissionsLoading && userRole) {
      const homeView = getHomeViewForRole(userRole);
      setActiveView(homeView);
    }
  }, [userRole, permissionsLoading]);

  // Helper to render view with permission check and Suspense for lazy loading
  const renderProtectedView = (viewName, component) => {
    if (!canAccessView(viewName)) {
      return <AccessDenied viewName={viewName} />;
    }
    return (
      <Suspense fallback={<PageLoader />}>
        {component}
      </Suspense>
    );
  };

  // Get orders by status
  const pendingAcceptanceOrders = getPendingAcceptanceOrders();
  const inProgressOrders = getOrdersByStatus('in_progress');
  const preparedOrders = getOrdersByStatus('prepared');
  const outForDeliveryOrders = getOrdersByStatus('out_for_delivery');
  const deliveredOrders = getOrdersByStatus('delivered');

  // Get stats
  const stats = getStats();

  // Handle status change
  const handleStatusChange = async (orderId, newStatus) => {
    // If changing to "out_for_delivery", check if it's a delivery order from "prepared" status
    if (newStatus === 'out_for_delivery') {
      const order = orders.find(o => o.id === orderId);

      // If it's a delivery order and currently prepared, show delivery boy assignment modal
      if (order && order.orderType === 'delivery' && order.status === 'prepared') {
        setOrderToAssign(order);
        setShowDeliveryAssignment(true);
        return; // Don't update status yet, wait for assignment
      }
    }

    // For all other status changes, update directly
    await updateOrderStatus(orderId, newStatus);
  };

  // Handle delivery boy assignment
  const handleDeliveryAssignment = async (orderId, deliveryBoyData) => {
    await assignDeliveryBoy(orderId, deliveryBoyData);
    setShowDeliveryAssignment(false);
    setOrderToAssign(null);
  };

  // Handle order acceptance
  const handleAcceptOrder = async (orderId) => {
    await acceptOrder(orderId);
  };

  // Handle order rejection
  const handleRejectOrder = async (orderId) => {
    await rejectOrder(orderId);
  };

  // Handle view details
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle date change
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  if (loading || permissionsLoading) {
    return (
      <div className={`modern-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar
          activeView={activeView}
          onViewChange={setActiveView}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <div className="main-content">
          <Header />
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>{permissionsLoading ? 'Loading permissions...' : 'Loading orders...'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`modern-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar
        activeView={activeView}
        onViewChange={setActiveView}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className="main-content">
        <Header />

        <div className="content-wrapper">
          {/* Dashboard Header */}
          <div className="page-header">
            <div className="page-title-section">
              <h1 className="page-title">
                {activeView === 'dashboard' ? 'Dashboard' : activeView === 'orders' ? 'Order Management' : 'Dashboard'}
              </h1>
              <p className="page-subtitle">Hi, Welcome back to Odisha Zaika Admin!</p>
            </div>

            {activeView === 'orders' && (
              <div className="filter-period">
                <div className="period-icon">📅</div>
                <div className="period-content">
                  <span className="period-label">Filter Periods</span>
                  <div className="date-selector-modern">
                    <button onClick={() => changeDate(-1)} className="btn-date-nav-modern">
                      ←
                    </button>
                    <span className="selected-date-modern">
                      {formatDate(selectedDate)}
                      {isToday && <span className="today-badge-modern">Today</span>}
                    </span>
                    <button
                      onClick={() => changeDate(1)}
                      className="btn-date-nav-modern"
                      disabled={isToday}
                    >
                      →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Render content based on active view */}
          {activeView === 'dashboard' && (
            <>
              {/* Stats Cards */}
              <div className="stats-grid">
                <StatsCard
                  icon="📦"
                  title="Total Orders"
                  value={stats.totalOrders}
                  trend="up"
                  trendValue="+15 (18 today)"
                  color="blue"
                />
                <StatsCard
                  icon="✅"
                  title="Total Delivered"
                  value={deliveredOrders.length}
                  trend="up"
                  trendValue="+11 (14 today)"
                  color="green"
                />
                <StatsCard
                  icon="❌"
                  title="Total Cancelled"
                  value={orders.filter(o => o.status === 'cancelled').length}
                  trend="down"
                  trendValue="+00 (05 today)"
                  color="red"
                />
                <StatsCard
                  icon="💰"
                  title="Total Revenue"
                  value={`₹${stats.totalRevenue}`}
                  trend="up"
                  trendValue="+8% (14 today)"
                  color="orange"
                />
              </div>

              {/* Analytics Section */}
              <Analytics
                orderData={{
                  delivered: deliveredOrders.length,
                  inProgress: inProgressOrders.length,
                  cancelled: orders.filter(o => o.status === 'cancelled').length,
                }}
                orders={orders}
              />

              {/* Outlet Comparison Section (only shown when viewing all outlets) */}
              {viewMode === 'all' && <OutletComparison />}
            </>
          )}

          {activeView === 'orders' && (
            <>
              {/* Quick Stats for Orders View */}
              <div className="stats-grid">
                <StatsCard
                  icon="📦"
                  title="Total Orders"
                  value={stats.totalOrders}
                  color="blue"
                />
                <StatsCard
                  icon="💰"
                  title="Revenue"
                  value={`₹${stats.totalRevenue}`}
                  color="green"
                />
                <StatsCard
                  icon="📊"
                  title="Avg Order"
                  value={`₹${Math.round(stats.avgOrderValue)}`}
                  color="purple"
                />
                <StatsCard
                  icon="⏱️"
                  title="Pending"
                  value={inProgressOrders.length + preparedOrders.length}
                  color="orange"
                />
              </div>

              {/* Kanban Board */}
              <div className="kanban-board">
        {/* Pending Acceptance Column */}
        <div className="kanban-column column-pending-acceptance">
          <div className="column-header">
            <h3>🔔 Pending Acceptance</h3>
            <span className="order-count">{pendingAcceptanceOrders.length}</span>
          </div>
          <div className="column-content">
            {pendingAcceptanceOrders.length > 0 ? (
              pendingAcceptanceOrders.map((order) => (
                <OrderAcceptanceCard
                  key={order.id}
                  order={order}
                  onAccept={handleAcceptOrder}
                  onReject={handleRejectOrder}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="empty-column">
                <p>No pending orders</p>
              </div>
            )}
          </div>
        </div>

        {/* In Progress Column */}
        <div className="kanban-column column-in-progress">
          <div className="column-header">
            <h3>In Progress</h3>
            <span className="order-count">{inProgressOrders.length}</span>
          </div>
          <div className="column-content">
            {inProgressOrders.length > 0 ? (
              inProgressOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onStatusChange={handleStatusChange}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="empty-column">
                <p>No orders in progress</p>
              </div>
            )}
          </div>
        </div>

        {/* Prepared Column */}
        <div className="kanban-column column-prepared">
          <div className="column-header">
            <h3>Prepared</h3>
            <span className="order-count">{preparedOrders.length}</span>
          </div>
          <div className="column-content">
            {preparedOrders.length > 0 ? (
              preparedOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onStatusChange={handleStatusChange}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="empty-column">
                <p>No prepared orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Out for Delivery Column */}
        <div className="kanban-column column-out-for-delivery">
          <div className="column-header">
            <h3>Out for Delivery</h3>
            <span className="order-count">{outForDeliveryOrders.length}</span>
          </div>
          <div className="column-content">
            {outForDeliveryOrders.length > 0 ? (
              outForDeliveryOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onStatusChange={handleStatusChange}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="empty-column">
                <p>No orders out for delivery</p>
              </div>
            )}
          </div>
        </div>

        {/* Delivered Column */}
        <div className="kanban-column column-delivered">
          <div className="column-header">
            <h3>Finished</h3>
            <span className="order-count">{deliveredOrders.length}</span>
          </div>
          <div className="column-content">
            {deliveredOrders.length > 0 ? (
              deliveredOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onStatusChange={handleStatusChange}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="empty-column">
                <p>No delivered orders</p>
              </div>
            )}
          </div>
        </div>
              </div>
            </>
          )}

          {/* Customer Page */}
          {activeView === 'customers' && renderProtectedView('customers',
            <CustomerProvider>
              <Customers />
            </CustomerProvider>
          )}

          {/* Users Management Page */}
          {activeView === 'users' && renderProtectedView('users', <Users />)}

          {/* User Sync Utility Page */}
          {activeView === 'user-sync' && renderProtectedView('user-sync', <UserSync />)}

          {/* Order Detail Page */}
          {activeView === 'order-detail' && renderProtectedView('order-detail', <OrderDetail />)}

          {/* Thermal Printer Page */}
          {activeView === 'thermal-printer' && (
            <Suspense fallback={<PageLoader />}>
              <ThermalPrinter />
            </Suspense>
          )}

          {/* Analytics Page */}
          {activeView === 'analytics' && renderProtectedView('analytics', <AnalyticsPage />)}

          {/* Reviews Page */}
          {activeView === 'reviews' && renderProtectedView('reviews', <Reviews />)}

          {/* Menu Page */}
          {activeView === 'menu' && renderProtectedView('menu', <Menu />)}

          {/* Menu Migration Page */}
          {activeView === 'menu-migration' && renderProtectedView('menu-migration', <MenuMigration />)}

          {/* Calendar Page */}
          {activeView === 'calendar' && renderProtectedView('calendar', <Calendar />)}

          {/* Phone Order Page */}
          {activeView === 'phone-order' && renderProtectedView('phone-order', <PhoneOrder />)}

          {/* Chat Page */}
          {activeView === 'chat' && renderProtectedView('chat', <Chat />)}

          {/* Data Management Page */}
          {activeView === 'data-management' && renderProtectedView('data-management', <DataManagement />)}

          {/* Outlets Page */}
          {activeView === 'outlets' && renderProtectedView('outlets', <Outlets />)}

          {/* Settings Page */}
          {activeView === 'settings' && renderProtectedView('settings', <Settings />)}

          {/* New Redesigned Pages */}
          {activeView === 'orders-new' && renderProtectedView('orders-new', <Orders />)}
          {activeView === 'order-history' && renderProtectedView('order-history', <OrderHistory />)}
          {activeView === 'menu-editor' && renderProtectedView('menu-editor', <MenuEditor />)}
          {activeView === 'inventory' && renderProtectedView('inventory', <Inventory />)}

          {/* Finance Pages */}
          {activeView === 'payouts' && renderProtectedView('payouts', <Payouts />)}
          {activeView === 'transactions' && renderProtectedView('transactions', <Transactions />)}
          {activeView === 'invoices' && renderProtectedView('invoices', <Invoices />)}
          {activeView === 'outlet-finance-reports' && renderProtectedView('outlet-finance-reports', <OutletFinanceReports />)}

          {/* Offers Pages */}
          {activeView === 'create-offers' && renderProtectedView('create-offers', <CreateOffers />)}
          {activeView === 'track-offers' && renderProtectedView('track-offers', <TrackOffers />)}

          {/* Outlet Info Pages */}
          {activeView === 'about-outlet' && renderProtectedView('about-outlet', <AboutOutlet />)}
          {activeView === 'contact-details' && renderProtectedView('contact-details', <ContactDetails />)}
          {activeView === 'outlet-timings' && renderProtectedView('outlet-timings', <OutletTimings />)}
          {activeView === 'outlet-location' && renderProtectedView('outlet-location', <Location />)}
          {activeView === 'smart-link' && renderProtectedView('smart-link', <SmartLink />)}

          {/* Reporting Pages */}
          {activeView === 'live-tracking' && renderProtectedView('live-tracking', <LiveTracking />)}
          {activeView === 'business-reports' && renderProtectedView('business-reports', <BusinessReports />)}

          {/* Items Management Pages (Phase 7) */}
          {activeView === 'list-all-items' && renderProtectedView('list-all-items', <ListAllItems />)}

          {/* Delivery Management Pages (Phase 9) */}
          {activeView === 'delivery-partners' && renderProtectedView('delivery-partners', <DeliveryBoys />)}
          {activeView === 'delivery-tracking' && renderProtectedView('delivery-tracking', <DeliveryTracking />)}

          {/* Phase 10 Pages */}
          {activeView === 'kitchen-display' && renderProtectedView('kitchen-display', <KitchenDisplay />)}
          {activeView === 'stock-alerts' && renderProtectedView('stock-alerts', <StockAlerts />)}
          {activeView === 'manage-locations' && renderProtectedView('manage-locations', <ManageLocations />)}
          {activeView === 'role-permissions' && renderProtectedView('role-permissions', <RolePermissions />)}

          {/* Billing & Accounting Pages */}
          {activeView === 'billing-dashboard' && renderProtectedView('billing-dashboard', <BillingDashboard />)}
          {activeView === 'create-invoice' && renderProtectedView('create-invoice', <CreateInvoice />)}
          {activeView === 'gst-settings' && renderProtectedView('gst-settings', <GSTSettings />)}
          {activeView === 'expense-management' && renderProtectedView('expense-management', <ExpenseManagement />)}
          {activeView === 'profit-loss' && renderProtectedView('profit-loss', <ProfitLoss />)}
          {activeView === 'cash-flow' && renderProtectedView('cash-flow', <CashFlow />)}

          {/* New Feature Pages */}
          {activeView === 'loyalty' && renderProtectedView('loyalty', <LoyaltyProgram />)}
          {activeView === 'redemption-history' && renderProtectedView('redemption-history', <RedemptionHistory />)}
          {activeView === 'scheduled-orders' && renderProtectedView('scheduled-orders', <ScheduledOrders />)}
          {activeView === 'analytics/sales-forecasting' && renderProtectedView('analytics/sales-forecasting', <SalesForecasting />)}
          {activeView === 'analytics/customer-segmentation' && renderProtectedView('analytics/customer-segmentation', <CustomerSegmentation />)}
          {activeView === 'analytics/menu-engineering' && renderProtectedView('analytics/menu-engineering', <MenuEngineering />)}
          {activeView === 'analytics/staff-performance' && renderProtectedView('analytics/staff-performance', <StaffPerformance />)}
          {activeView === 'analytics/predictive-insights' && renderProtectedView('analytics/predictive-insights', <PredictiveInsights />)}

          {/* POS Management Pages */}
          {activeView === 'pos-settings' && renderProtectedView('pos-settings', <POSSettings />)}
          {activeView === 'tables' && renderProtectedView('tables', <AdminTableManagement />)}
          {activeView === 'terminals' && renderProtectedView('terminals', <AdminTerminalManagement />)}
        </div>
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrder(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Delivery Boy Assignment Modal */}
      {showDeliveryAssignment && orderToAssign && (
        <UserProvider>
          <DeliveryBoyAssignmentModal
            order={orderToAssign}
            onClose={() => {
              setShowDeliveryAssignment(false);
              setOrderToAssign(null);
            }}
            onAssign={handleDeliveryAssignment}
          />
        </UserProvider>
      )}
    </div>
  );
};

export default Dashboard;
